import React from 'react';
import { archiveBuilding, deleteBuildingDot1x, updateBuilding } from 'shared/api/buildings';
import ArchiveSwitch from 'shared/components/ArchiveSwitch';
import { CardControls, CardSection } from 'shared/components/cardControls';
import { useAuth, useNotification, useRequest } from 'shared/hooks';
import { isBlankString } from 'shared/utils';
import { BoxWrapper } from 'shared/components/data-display/GridFormatter';
import { DataReducerActions } from 'shared/reducers';
import { INTERNET_TYPES } from 'shared/constants/building';
import BuildingInfo from './BuildingInfo';

export function validateBuildingData(submittedData, notification) {
  let isValid = true;
  if (('name' in submittedData) && isBlankString(submittedData.name)) {
    notification.pushError('Requires a valid property name');
    isValid = false;
  }

  if (('address_1' in submittedData) && isBlankString(submittedData.address_1)) {
    notification.pushError('Requires a valid address');
    isValid = false;
  }

  return isValid;
}

/**
 * @param {Function} buildingDispatch - Dispatch function for building state
 * @param {Object<string, *>} buildingState - The building state object
 * @param {Function} reloadBuilding - Function to reload building data
 */
export default function BuildingView({
  buildingDispatch,
  buildingState,
  reloadBuilding,
}) {
  const {
    data: {
      id: buildingId,
      archived_at: archivedAt,
    },
    updatedData,
  } = buildingState;
  const { user } = useAuth();
  const {
    request,
    isLoading,
  } = useRequest();
  const notification = useNotification();

  const saveChanges = async () => {
    if (validateBuildingData(updatedData, notification)) {
      const { data: { id } } = buildingState;
      const body = { ...buildingState.updatedData };
      body.updated_by = user.username;
      const updatedBuilding = await request(updateBuilding(id, body), 'Updated building');
      if (updatedBuilding) {
        if (
          body.service_type === INTERNET_TYPES.none
          && body.dot1x !== null
          && window.confirm('Building Internet was updated to NONE. Would you like to clear associated 802.1x settings?')) {
          const deleteResponse = await request(deleteBuildingDot1x(id), 'Removed Building Dot1x');
          if (deleteResponse) {
            updatedBuilding.dot1x = null;
          }
        }
        reloadBuilding();
      }
    }
    return null;
  };

  return (
    <CardSection editEnabled={buildingState.editEnabled} saveChanges={saveChanges}>
      <CardControls
        clearChanges={() => buildingDispatch({ action: DataReducerActions.clear })}
        disabled={isLoading}
        dirty={buildingState.dirty}
        editEnabled={buildingState.editEnabled}
      >
        <ArchiveSwitch
          afterToggle={reloadBuilding}
          archivedAt={archivedAt}
          requestBody={archiveBuilding(buildingId, !archivedAt, user.username)}
          showToggle={buildingState.editEnabled}
        />
      </CardControls>
      <BoxWrapper>
        <BuildingInfo
          building={buildingState.data}
          buildingDispatch={buildingDispatch}
          updatedData={buildingState.updatedData}
          updateValue={(key, value) => buildingDispatch({
            action: DataReducerActions.update,
            payload: {
              key,
              value,
            },
          })}
          editEnabled={buildingState.editEnabled}
        />
      </BoxWrapper>
    </CardSection>
  );
}
